<script setup lang="ts">
import { isEmpty } from 'lodash-es'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string | null
    confirmDialog?: boolean
    prompt?: IPChat.Components.DialogOptions['prompt']
    confirm?: IPChat.Components.DialogButtonsOptions
    cancel?: IPChat.Components.DialogButtonsOptions
    backdropClose?: boolean
    width?: string
    component?: {
      render: globalThis.Component
      props?: Record<string, any>
    }
  }>(),
  {
    confirmDialog: false,
    cancel: false,
    backdropClose: false,
    confirm: () => {
      return {
        hold: false,
        holdTime: 10000,
      }
    },
  },
)

const emits = defineEmits<{
  (e: 'close', payload?: any): void
  (e: 'confirm', payload?: any): void
}>()
const open = defineModel<boolean>({ required: false, default: false })
const slot = useSlots()

const disabledConfirmButton = ref(false)
const promptInputRef = ref<HTMLInputElement | null>(null)
const promptValue = ref<string | undefined>(undefined)

function submitConfirm() {
  if (props.prompt && props.prompt.required && isEmpty(promptValue.value)) {
    if (promptInputRef.value) {
      promptInputRef.value.classList.add('tw-daisy-input-error')
      promptInputRef.value.focus()
    }
    return
  }

  emits('confirm', promptValue.value)
}

function close() {
  open.value = false
  emits('close')
}

onBeforeMount(() => {
  if (typeof props.confirm === 'object' && props.confirm.hold) {
    setTimeout(() => {
      disabledConfirmButton.value = false
    }, props.confirm.holdTime)
  }
})

onMounted(() => {
  open.value = true
})
</script>
<template>
  <Teleport to="body">
    <section
      v-if="open"
      class="modal-backdrop"
      :class="{ open }"
      @click.prevent="
        () => {
          if (backdropClose) close()
        }
      "
    >
      <section class="modal-container" v-bind="$attrs" @click.stop>
        <section
          class="modal-content scroller"
          ref="elConfirmDialog"
          id="elConfirmDialog"
        >
          <header v-if="title" class="modal-header">
            <div class="tw-flex tw-flex-col">
              <h4 class="tw-text-[1.1rem] tw-font-bold">
                {{ title }}
              </h4>
              <p v-if="subtitle" class="tw-text-sm">
                {{ subtitle }}
              </p>
            </div>

            <span class="icon-container" @click.prevent="close">
              <MaterialIcon name="close" class="text-slate-600" />
            </span>
          </header>

          <!--- content -->
          <section class="tw-w-full tw-flex tw-flex-col tw-flex-wrap tw-p-4">
            <slot v-if="slot.content" name="content" />
            <component
              v-if="component?.render"
              :is="component.render"
              v-bind="component.props"
            />
            <template v-else-if="prompt">
              <div class="tw-mt-4">
                <label
                  ref="promptInputRef"
                  class="tw-daisy-input tw-daisy-input-bordered tw-flex tw-items-center tw-gap-2"
                >
                  <MaterialIcon v-if="prompt.icon" :name="prompt.icon" />
                  <input
                    v-model="promptValue"
                    :maxlength="prompt.maxlength"
                    class="tw-grow tw-daisy-input-sm"
                    :required="prompt.required"
                    :type="prompt.type"
                    :placeholder="prompt.placeholder"
                  />
                  <span
                    v-if="prompt.required"
                    class="tw-daisy-badge tw-daisy-badge-error"
                  >
                    Obrigatório
                  </span>
                </label>
              </div>
            </template>
          </section>

          <section v-if="confirmDialog" class="modal-footer">
            <section class="modal-actions">
              <DefaultButton
                v-if="typeof cancel === 'object'"
                @click.prevent="close"
                color="error"
                :label="cancel?.label || 'Fechar'"
                :disabled="cancel?.disabled"
              />
              <DefaultButton
                v-if="typeof confirm === 'object'"
                :disabled="disabledConfirmButton || confirm?.disabled"
                :loading="confirm?.loading"
                @click.prevent="submitConfirm"
                color="neutral"
                :label="confirm?.label || 'Confirmar'"
              />
            </section>
          </section>
        </section>
      </section>
    </section>
  </Teleport>
</template>
<style scoped>
@import '../../css/modal.scss';
</style>
